/**
 * Hinweis, der Benachrichtigungen einschaltbar macht — für Menschen ohne
 * technischen Hintergrund.
 *
 * Hintergrund: Edge und Chrome zeigen die Erlaubnis-Nachfrage standardmäßig nur
 * als kleines, durchgestrichenes Glocken-Symbol in der Adressleiste. Wer das
 * nicht kennt, wird nie gefragt und bekommt nie eine Benachrichtigung — ohne
 * jeden Hinweis. Deshalb fragt KMVS sichtbar in der eigenen Oberfläche und
 * erklärt Schritt für Schritt, was zu tun ist, wenn der Browser mauert.
 *
 * Zwei Darstellungen:
 *  - `balken`: schmale Leiste unter der Kopfzeile (vertagbar, 7 Tage Ruhe),
 *  - `karte`:  im Mitteilungszentrum, solange die Einrichtung unvollständig ist.
 */
import * as React from "react";
import { Alert20Regular, Dismiss16Regular } from "@fluentui/react-icons";
import { anleitungFuer, NUTZEN_SATZ } from "./notificationSetup";
import { useNotificationSetup } from "./useNotificationSetup";

const AUBERGINE = "#7d2169";
const SCHRIFT = "'Hanken Grotesk', 'Segoe UI', Tahoma, sans-serif";

/** Zustände, die im Balken auftauchen. „nicht-unterstuetzt“ bleibt draußen:
 *  daran kann der Nutzer im Zweifel nichts ändern, es wäre reines Rauschen. */
const IM_BALKEN = new Set(["fragen-moeglich", "leise-abgefangen", "blockiert", "abo-fehlt", "ios-webapp-noetig"]);

/** Schritte erst zeigen, wenn der einfache Weg (ein Klick) nicht gereicht hat. */
const MIT_SCHRITTEN = new Set(["leise-abgefangen", "blockiert", "abo-fehlt", "ios-webapp-noetig"]);

const knopfStil = (haupt: boolean): React.CSSProperties => ({
  appearance: "none",
  cursor: "pointer",
  fontFamily: SCHRIFT,
  fontSize: "0.85rem",
  fontWeight: 600,
  padding: "8px 14px",
  minHeight: 36,
  borderRadius: 9,
  whiteSpace: "nowrap",
  border: haupt ? "none" : `1px solid rgba(125,33,105,0.28)`,
  background: haupt ? AUBERGINE : "transparent",
  color: haupt ? "#ffffff" : AUBERGINE,
});

export const NotificationSetupHinweis: React.FC<{
  /** Nur prüfen/anzeigen, wenn angemeldet. */
  aktiv: boolean;
  variante: "balken" | "karte";
}> = ({ aktiv, variante }) => {
  const { zustand, browser, laeuft, fehler, erlauben, erneutVersuchen, spaeter, vertagt } =
    useNotificationSetup(aktiv);

  if (!aktiv || !zustand || zustand === "aktiv") return null;
  if (variante === "balken" && (vertagt || !IM_BALKEN.has(zustand))) return null;

  const anleitung = anleitungFuer(browser, zustand);
  if (!anleitung) return null;

  const zeigeSchritte = MIT_SCHRITTEN.has(zustand);
  const aktionKnopf =
    anleitung.aktion === "erlauben" ? (
      <button type="button" style={knopfStil(true)} disabled={laeuft} onClick={() => void erlauben()}>
        {laeuft ? "Einen Moment …" : "Benachrichtigungen erlauben"}
      </button>
    ) : anleitung.aktion === "erneut-versuchen" ? (
      <button type="button" style={knopfStil(true)} disabled={laeuft} onClick={() => void erneutVersuchen()}>
        {laeuft ? "Einen Moment …" : "Erneut versuchen"}
      </button>
    ) : null;

  const inhalt = (
    <>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10, minWidth: 0 }}>
        <span style={{ color: AUBERGINE, flexShrink: 0, marginTop: 1 }}>
          <Alert20Regular />
        </span>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: "0.92rem", color: "#241522" }}>{anleitung.titel}</div>
          <div style={{ fontSize: "0.85rem", color: "#5b4a58", marginTop: 2 }}>{NUTZEN_SATZ}</div>
          {zeigeSchritte && (
            <ol style={{ margin: "8px 0 0", paddingLeft: 20, fontSize: "0.85rem", color: "#3d2c3a", lineHeight: 1.5 }}>
              {anleitung.schritte.map((s) => (
                <li key={s}>{s}</li>
              ))}
            </ol>
          )}
          {fehler && (
            <div style={{ marginTop: 6, fontSize: "0.78rem", color: "#8a6a84", wordBreak: "break-word" }}>
              Meldung des Browsers: {fehler}
            </div>
          )}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0, flexWrap: "wrap" }}>
        {aktionKnopf}
        {variante === "balken" && (
          <button type="button" style={knopfStil(false)} onClick={spaeter} aria-label="Hinweis für sieben Tage ausblenden">
            <Dismiss16Regular style={{ verticalAlign: "-2px", marginRight: 4 }} />
            Später
          </button>
        )}
      </div>
    </>
  );

  if (variante === "karte") {
    return (
      <div
        role="region"
        aria-label="Benachrichtigungen einrichten"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: "12px 14px",
          marginBottom: 10,
          borderLeft: `3px solid ${AUBERGINE}`,
          background: "rgba(248,238,245,0.85)",
          borderRadius: 12,
          fontFamily: SCHRIFT,
        }}
      >
        {inhalt}
      </div>
    );
  }

  return (
    <div
      role="region"
      aria-label="Benachrichtigungen einrichten"
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 14,
        flexWrap: "wrap",
        padding: "10px 14px",
        margin: "6px 4px 0",
        borderRadius: 12,
        border: "1px solid rgba(125,33,105,0.18)",
        background: "linear-gradient(180deg, rgba(248,238,245,0.95) 0%, rgba(248,238,245,0.75) 100%)",
        fontFamily: SCHRIFT,
      }}
    >
      {inhalt}
    </div>
  );
};

export default NotificationSetupHinweis;
